const $ = require('jquery');

const ApiError = require('./utils/ApiError');

const constants = require('./data/constants');
const User = require('./data/User');
const File = require('./data/File');
const Files = require('./data/Files');
const Segment = require('./data/Segment');

const commands = {
  login: 'LOGIN',
  fileList: 'FILELIST',
  match: 'MATCH',
  createFile: 'CREATEFILE',
  deleteFile: 'DELETEFILE',
  fileContents: 'DIFF',
  addSegment: 'ADD',
  changeSegment: 'CHANGE',
  deleteSegment: 'DELETE',
};

/**
 * @class Api
 * Interface to the OpenLR server
 */
class Api {
  constructor(localization) {
    this.serverUrlTomTom = 'https://detoureditor.maptrip.de/server/api/tomtom/';
    this.serverUrlHere = 'https://detoureditor.maptrip.de/server/api/here/';

    this.STATUS_OK = 'OK';
    this.localization = localization;
  }

  setDefaultServer(server) {
    localStorage.setItem(constants.PROVIDER_STORAGE_KEY, server);
  }

  /**
   * Issues a login command to the server and creates a User object if it succeeds
   *
   * @param company
   * @param username
   * @param password
   * @return {Promise<any>}
   */
  login(company, username, password, server) {
    return new Promise((resolve, reject) => {
      this.makeHTTPRequest(
        this.buildUrl('login', server),
        'GET',
        {
          COMPANY: company,
          USER: username,
          PASS: password,
        },
      ).then((response) => {
        const status = $(response).find('STATUS');

        resolve(new User(company, username, $(status).attr('sessionid'), $(status).attr('flags')));
      }).catch((error) => {
        reject(error);
      });
    });
  }

  /**
   * Gets a list of files based on the current session ID
   * @param sessionId
   * @return {Promise<any>}
   */
  getFileList(sessionId) {
    return new Promise((resolve, reject) => {
      this.makeHTTPRequest(
        this.buildUrl('fileList'), 'GET', { sessionid: sessionId },
      ).then((response) => {
        const files = new Files();

        $(response).find('file').each(function () {
          files.add(new File($(this).attr('id'), $(this).attr('name'), $(this).attr('ver'), $(this).attr('user'), $(this).attr('company'), null, $(this).attr('active')));
        });

        resolve(files);
      }).catch((error) => {
        reject(error);
      });
    });
  }

  /**
   * Gets the contents of a file and returns a list of segments
   *
   * @param sessionId
   * @param {File} file
   * @return {Promise<File>}
   */
  getFileContents(sessionId, file) {
    return new Promise((resolve, reject) => {
      this.makeHTTPRequest(
        this.buildUrl('fileContents'), 'GET', { sessionid: sessionId, FILEID: file.id, VER: -1 },
      ).then((response) => {
        const segments = [];

        $(response).find('delta[op="change"]').each((index, element) => {
          let polyline = $(element).attr('polyline');
          const olr = $(element).attr('olr');

          const attributesRaw = $(element).find('att');
          const id = $(element).attr('id');
          polyline = polyline.split(',');

          const coordinates = Api.extractPolylineCoordinates(polyline);

          const attributes = {};

          attributesRaw.each(function () {
            attributes[$(this).attr('name')] = $(this).attr('val');
          });

          segments.push(new Segment(id, coordinates, olr, attributes, constants.SEGMENT_PUBLISHED));
        });

        resolve(new File(file.id, file.name, file.version, file.user, file.company, segments));
      }).catch((error) => {
        reject(error);
      });
    });
  }

  /**
   * Matches a set of coordinates belonging to a segment
   *
   * @param sessionId
   * @param segment
   *
   * @return {Promise<any>}
   */
  matchSegment(sessionId, segment) {
    return new Promise((resolve, reject) => {
      this.makeHTTPRequest(
        this.buildUrl('match'), 'GET', { sessionid: sessionId, POLYLINE: segment.coordinatesString },
      ).then((response) => {
        const polyline = $(response).find('matched').attr('polyline').split(',');
        const olr = $(response).find('matched').attr('olr');

        const coordinates = Api.extractPolylineCoordinates(polyline);

        resolve(new Segment(null, coordinates, olr, segment.attributes, constants.SEGMENT_NEW));
      }).catch((error) => {
        reject(error);
      });
    });
  }

  /**
   * Adds a segment to a file
   *
   * @param sessionId
   * @param fileId
   * @param {Segment} segment
   * @param preMatched
   *
   * @return {Promise<any>}
   */
  addSegment(sessionId, fileId, segment, preMatched) {
    return new Promise((resolve, reject) => {
      const args = {
        sessionid: sessionId,
        OLR: segment.olr,
        FILEID: fileId,
        NUMATT: segment.attributesCount,
      };

      if (preMatched) {
        args.POLYLINE = segment.coordinatesString;
      }

      let index = 1;

      Object.keys(segment.attributes).forEach((name) => {
        args[`ATT${index}`] = name;
        args[`VAL${index}`] = segment.attributes[name];
        index += 1;
      });

      this.makeHTTPRequest(
        this.buildUrl('addSegment'), 'POST', args,
      ).then((response) => {
        const id = $(response).find('STATUS').attr('id');

        resolve(new Segment(
          id,
          segment.coordinates,
          segment.olr,
          segment.attributes,
          constants.SEGMENT_PUBLISHED,
        ));
      }).catch((error) => {
        reject(error);
      });
    });
  }

  /**
   * Adds a segment to a file
   *
   * @param sessionId
   * @param fileId
   * @param segment
   *
   * @return {Promise<any>}
   */
  changeSegment(sessionId, fileId, segment) {
    return new Promise((resolve, reject) => {
      const args = {
        sessionid: sessionId,
        FILEID: fileId,
        ID: segment.id,
        NUMATT: segment.attributesCount,
      };
      let index = 1;

      Object.keys(segment.attributes).forEach((name) => {
        args[`ATT${index}`] = name;
        args[`VAL${index}`] = segment.attributes[name];
        index += 1;
      });

      this.makeHTTPRequest(
        this.buildUrl('changeSegment'), 'GET', args,
      ).then(() => {
        resolve(new Segment(
          segment.id,
          segment.coordinates,
          segment.olr,
          segment.attributes,
          constants.SEGMENT_PUBLISHED,
        ));
      }).catch((error) => {
        reject(error);
      });
    });
  }

  /**
   * Adds a segment to a file
   *
   * @param sessionId
   * @param fileId
   * @param segment
   *
   * @return {Promise<any>}
   */
  deleteSegment(sessionId, fileId, segment) {
    return new Promise((resolve, reject) => {
      this.makeHTTPRequest(
        this.buildUrl('deleteSegment'), 'POST', { sessionid: sessionId, FILEID: fileId, ID: segment.id },
      ).then(() => {
        resolve(true);
      }).catch((error) => {
        reject(error);
      });
    });
  }

  /**
   * Creates a file on the server
   *
   * @param {string} sessionId
   * @param {string} fileName
   * @param {string} user
   * @param {string} company
   *
   * @return {Promise<File>}
   */
  createFile(sessionId, fileName, user, company) {
    return new Promise((resolve, reject) => {
      this.makeHTTPRequest(
        this.buildUrl('createFile'), 'GET', { sessionid: sessionId, FILENAME: fileName },
      ).then((response) => {
        const id = $(response).find('STATUS').attr('fileid');

        resolve(new File(id, fileName, 0, user, company));
      }).catch((error) => {
        reject(error);
      });
    });
  }

  /**
   * Creates a file on the server
   *
   * @param {String} sessionId
   * @param {Integer} fileId
   *
   * @return {Promise<any>}
   */
  deleteFile(sessionId, fileId) {
    return new Promise((resolve, reject) => {
      this.makeHTTPRequest(
        this.buildUrl('deleteFile'), 'GET', { sessionid: sessionId, FILEID: fileId },
      ).then(() => {
        resolve(true);
      }).catch((error) => {
        reject(error);
      });
    });
  }

  /**
   * General method that does a HTTP request to the server
   * @private
   *
   * @param url
   * @param method
   * @param data
   * @return {Promise<any>}
   */
  makeHTTPRequest(url, method, data) {
    return new Promise((resolve, reject) => {
      // noinspection JSIgnoredPromiseFromCall
      $.ajax({
        method, url, data, contentType: 'text/plain', dataType: 'xml',
      })
        .done((responseXML) => {
          const status = $(responseXML).find('STATUS')[0];

          if ($(status).attr('value') !== this.STATUS_OK) {
            let code = -1;
            let errorMessage = 'Failed with undefined error message';

            if ($(status).attr('errorCode')) {
              code = $(status).attr('errorCode');
            }

            if ($(status).attr('message')) {
              errorMessage = $(status).attr('message');
            }

            reject(new ApiError(code, errorMessage, this.localization));
          }

          resolve($(responseXML).find('IWXML')[0]);
        })
        .fail((jqXHR) => {
          let code = -1;
          let errorMessage = `Request to server failed with status ${jqXHR.status}`;

          const status = $(jqXHR.responseXML).find('STATUS')[0];

          if ($(status).attr('errorCode')) {
            code = $(status).attr('errorCode');
          }

          if ($(status).attr('message')) {
            errorMessage = $(status).attr('message');
          }
          reject(new ApiError(code, errorMessage, this.localization));
        });
    });
  }

  buildUrl(command, server) {
    const serverUrl = this.getServerUrl(server);

    if (!Object.prototype.hasOwnProperty.call(commands, command)) {
      throw new Error(`Command "${command}" is not defined`);
    }

    return `${serverUrl}?CMD=${commands[command]}&ACAO=true`;
  }

  getServerUrl(server) {
    if(!server) {
      server = localStorage.getItem(constants.PROVIDER_STORAGE_KEY);
    }

    if(!server) return this.serverUrlTomTom;

    switch (server) {
      case constants.PROVIDER_TOMTOM:
      default:
        return this.serverUrlTomTom;

      case constants.PROVIDER_HERE:
        return this.serverUrlHere;
    }
  }

  static extractPolylineCoordinates(polyline) {
    return polyline.reduce((reduceCoordinates, coordinate, index) => {
      if (index % 2 === 0) {
        reduceCoordinates.push(
          new IWCoordinate(polyline[index], polyline[index + 1], IWCoordinate.WGS84),
        );
      }

      return reduceCoordinates;
    }, []);
  }
}

module.exports = Api;
